import { Button, CIActionBtnGroup, CIActionGuard, CITableGrid, useUserPermission } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { requiredAuthPermission } from '../../../../../common/constants/UserPermissionConstants';
import { DEFAULT_PAGE_NUM } from '../../../../constants';
import { IPatternDefinition, ISourceRegistryListProps } from '../../../../models';
import PatternRegistryForm from '../pattern-registry-form/PatternRegistryForm';
import classes from './PatternList.module.scss';

const PatternList: React.FC<ISourceRegistryListProps> = ({ view, setPatternList, patternList }: ISourceRegistryListProps) => {
  const { t: translate } = useTranslation();
  const PATTERN_LIST = {
    LBL_PATTERN_NAME: translate('patternList.LBL_PATTERN_NAME'),
    NAME_PATTERN: 'name',
    LBL_EVENT_NAME: translate('patternList.LBL_EVENT_NAME'),
    NAME_EVENT_NAME: 'eventName',
    LBL_SOURCE_NAME: translate('patternList.LBL_SOURCE_NAME'),
    NAME_SOURCE: 'sourceName',
    LBL_LAST_MOD: translate('patternList.LBL_LAST_MOD'),
    NAME_LAST_MOD: 'updatedOn',
    LBL_ACTION: 'action',
    ORDER_ASC: 'ASC',
    ORDER_DESC: 'DESC',
    FOOTER_TYPE: 'paging'
  };
  const [showPatternReg, setShowPatternReg] = useState<boolean>(false);

  const [viewMode, setViewMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [patternRowData, setPatternRowData] = useState<IPatternDefinition>();
  const [tempPatternList, setTempPatternList] = useState<IPatternDefinition[]>();
  const openPatternReg = () => {
    setShowPatternReg(true);
    setViewMode(false);
    setEditMode(false);
    setPatternRowData(undefined);
  };

  const { permissionObject } = useUserPermission();
  const [page, setPage] = useState<number>(0);
  const [item, setItem] = useState<number>(5);
  const sortedObj = { sortedName: PATTERN_LIST.NAME_LAST_MOD, sortedOrder: PATTERN_LIST.ORDER_ASC };

  /**
  * To render eventName from Definition JSON
  * @param row
  * @returns JSX element
  */
  const renderEventDefAction = (row: IPatternDefinition) => (
    <div className={classes['pattern-event-wrapper']}>
      <span className={classes['cp-capsule-wrapped-title']} title={String(translate('patternList.LBL_EVENT_NAME'))}>
        {row.eventName}
      </span>
      <p className={classes['event-version']}>
        {translate('event_list_component.txt_version', {
          replace: {
            version: row.eventVersion
          }
        })}
      </p>
    </div>
  );

  /**
  * To navigate to view pattern screen
  * @param name
  */
  const navigateToPatternRegistryView = (row: IPatternDefinition) => {
    setShowPatternReg(true);
    setViewMode(true);
    setEditMode(false);
    setPatternRowData(row);
  };

  /**
  * To navigate to edit pattern screen
  * @param name
  */
  const navigateToPatternRegistryEdit = (row: IPatternDefinition) => {
    setShowPatternReg(true);
    setEditMode(true);
    setViewMode(false);
    setPatternRowData(row);
  };

  /**
  * To render column action buttons
  * @param row
  * @returns JSX element
  */
  const renderColAction = (row: IPatternDefinition) => (
    <CIActionBtnGroup buttons={[
      { label: 'view', key: 'viewIcon', onClick: () => navigateToPatternRegistryView(row), testId: 'view-btn' },
      { label: 'edit', key: 'editIcon', onClick: () => navigateToPatternRegistryEdit(row), testId: 'edit-btn', show: CIActionGuard([requiredAuthPermission.CP.edit], permissionObject.permission) && !view }
    ]}
    />
  );

  /**
 * To render pattern name in tooltip
 * @param row
 * @returns JSX element
 */
  const renderPatternName = (row: IPatternDefinition) => (
    <div className={classes['pattern-name-wrapper']}>
      <span className={classes['cp-capsule-wrapped-title']} title={String(translate('patternList.LBL_PATTERN_NAME'))}>
        {row.patternName}
      </span>
    </div>
  );

  /**
   * @method liftPatternData
   * @description Lifts the pattern data and updates the pattern list.
   * If in edit mode, updates the existing pattern with the same pattern name.
   * If not in edit mode, adds the pattern to the beginning of the pattern list.
   * @param pattern The pattern to be lifted and updated.
   */
  const liftPatternData = (pattern: IPatternDefinition) => {
    const tempPattern = [...patternList];
    if (editMode) {
      const index = tempPattern.findIndex((x) => x.patternName === pattern.patternName);
      tempPattern.splice(index, 1);
    }
    tempPattern.unshift(pattern);
    setPage(DEFAULT_PAGE_NUM);
    setPatternList(tempPattern);
    paginationData(editMode ? page : DEFAULT_PAGE_NUM, item);
  };

  /**
   * @method paginationData
   * @description Updates the pagination data for the pattern list.
   * @param pageNo - The page number.
   * @param item - The number of items per page.
   */
  const paginationData = (pageNo: number, item: number) => {
    const temp = patternList.slice(pageNo * item, (pageNo + 1) * item);
    setTempPatternList(temp);
  };

  /**
   * @method getTitle
   * @description Returns the title based on the current mode.
   * If in view mode, returns the translated string 'pattern_registry_form.view_pattern'.
   * If in edit mode, returns the translated string 'pattern_registry_form.updatePattern'.
   * If in add mode, returns the translated string 'pattern_registry_form.addPattern'.
   * @returns The title string.
   */
  const getTitle = (): string => {
    if (viewMode) return translate('pattern_registry_form.view_pattern');
    return (editMode ? translate('pattern_registry_form.updatePattern') : translate('pattern_registry_form.addPattern'));
  };

  /**
   * @method onPageNoUpdate
   * @description Updates the current page number and triggers the paginationData function.
   * @param pageNo - The new page number.
   */
  const onPageNoUpdate = (pageNo: number) => {
    setPage(pageNo);
  };

  /**
   * @method getItemPerPage
   * @description Sets the number of items per page and updates the pagination data.
   * @param items The number of items per page.
   */
  const getItemPerPage = (items: number) => {
    setItem(items);
  };

  const footerOptionsObj = {
    itemsPerPage: item,
    currentPageNumber: page,
    numberOfPages: Math.ceil(patternList.length / item),
    selectItems: [5, 10],
    onCurrentPageNumberChange: onPageNoUpdate,
    onItemsPerPageChange: getItemPerPage
  };

  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  // to reset the queryParams when tab number changes
  useEffect(() => {
    const temp = patternList.slice(page * item, (page + 1) * item);
    setTempPatternList(temp);
  }, []);

  useEffect(() => {
    const temp = patternList.slice(page * item, (page + 1) * item);
    setTempPatternList(temp);
  }, [patternList]);

  useEffect(() => {
    paginationData(page, item);
  }, [item, page]);

  return (
    <div className={classes['pr-list-container']}>
      <div className={classes.filters}>
        <div className={classes['search-filter-div']}>
          <div className={classes['form-header']}>
            <div className={classes['header-title']}>
              {translate('pattern_registry_form.pattern_txt')}
            </div>
            {CIActionGuard([requiredAuthPermission.CP.edit], permissionObject.permission) && !view && (
              <Button
                variant="secondary"
                data-testid="sr-add-form-btn"
                onClick={openPatternReg}
              >
                {translate('source_registry_form.add_pattern_txt')}
              </Button>
            )}
          </div>
          <div className={classes['header-subtitle']}>
            {translate('pattern_registry_form.form_sub_title')}
          </div>
        </div>
      </div>
      <div className={classes['pattern-list']} data-testid="pattern-list">
        {patternList?.length !== 0 && (
          <CITableGrid
            headers={[
              {
                label: PATTERN_LIST.LBL_PATTERN_NAME,
                name: PATTERN_LIST.NAME_PATTERN,
                options: { breakPointWidth: 6 },
                columnData: { field: PATTERN_LIST.NAME_PATTERN, render: renderPatternName }
              },
              {
                label: PATTERN_LIST.LBL_EVENT_NAME,
                name: PATTERN_LIST.NAME_EVENT_NAME,
                options: { breakPointWidth: 4 },
                columnData: { field: PATTERN_LIST.NAME_EVENT_NAME, render: renderEventDefAction }
              },
              {
                label: '',
                name: PATTERN_LIST.LBL_ACTION,
                options: { breakPointWidth: 1 },
                columnData: { field: PATTERN_LIST.LBL_ACTION, render: renderColAction, className: 'render-action' }
              }
            ]}
            sorted={sortedObj}
            tableData={tempPatternList || []}
            footer={footerObj}
          />
        )}
      </div>
      <Modal
        size="xl"
        show={showPatternReg}
        onHide={() => {
          setShowPatternReg(false);
        }}
        backdrop="static"
        keyboard={false}
        aria-label={getTitle()}
        centered
        alignToCentre
      >
        <Modal.Header closeButton className={classes['pr-modal-header']}>
          {getTitle()}
        </Modal.Header>
        <PatternRegistryForm
          isView={viewMode}
          isEdit={editMode}
          urlParam={{ patternname: patternRowData?.patternName, viewResponse: patternRowData }}
          shiftPatternData={liftPatternData}
          setShowPatternReg={setShowPatternReg}
          patternList={patternList}
        />
      </Modal>
    </div>
  );
};

export default PatternList;
